import { z } from 'zod'
import { calendarWorkoutSchema, parseCalendarWorkout } from './calendarWorkoutSync'
import type { MetricRecord, SleepRecord, WorkoutRecord } from './types'
import { parseMetricInput, parseSleepInput, parseWorkoutInput } from './validation'
import { shortcutSleepRecords, shortcutSleepSampleSchema } from './shortcutSleep'

export const MAX_SYNC_BYTES = 256 * 1024
const instant = z.iso.datetime({ offset: true })
const externalId = z.string().min(1).max(160)
const zone = z.string().min(1).max(80)
const metric = z.strictObject({
  externalId, type: z.enum(['weight', 'bodyFat', 'activeEnergy']),
  value: z.number().finite(), recordedAt: instant, timeZone: zone,
})
const sleep = z.strictObject({ externalId, startAt: instant, endAt: instant, wakeTimeZone: zone })
const workout = z.strictObject({
  externalId, activityType: z.enum(['strength', 'running', 'walking', 'cycling', 'swimming', 'yoga', 'other']),
  startedAt: instant, timeZone: zone, durationMinutes: z.number().finite(),
  activeEnergyKcal: z.number().finite().optional(),
})
export const shortcutSnapshotSchema = z.strictObject({
  version: z.literal(1), exportedAt: instant,
  metrics: z.array(metric).max(1000), sleeps: z.array(sleep).max(31), workouts: z.array(workout).max(300),
  calendarWorkouts: z.array(calendarWorkoutSchema).max(300).optional(),
  sleepSamples: z.array(shortcutSleepSampleSchema).max(2000).optional(),
})
export type ShortcutSnapshot = z.infer<typeof shortcutSnapshotSchema>
export interface ShortcutRecords {
  metrics: MetricRecord[]
  sleeps: SleepRecord[]
  workouts: WorkoutRecord[]
}

/** A complete, rolling 30-day snapshot. Never mixed into manual storage/backup. */
export function parseShortcutSnapshot(input: unknown, now = new Date()): { snapshot: ShortcutSnapshot; records: ShortcutRecords } {
  const snapshot = shortcutSnapshotSchema.parse(input)
  snapshot.exportedAt = new Date(snapshot.exportedAt).toISOString()
  const exportedAt = Date.parse(snapshot.exportedAt)
  if (exportedAt > now.getTime() + 300_000) throw new Error('导出时间不能在未来')
  const keys = new Set<string>()
  const metadata = (kind: string, id: string, at: string) => {
    const key = `${kind}:${id}`
    if (keys.has(key)) throw new Error('快照含重复记录标识')
    keys.add(key)
    const time = Date.parse(at)
    if (time < exportedAt - 30 * 86400_000 || time > exportedAt + 300_000) {
      throw new Error('记录超出快照的最近 30 天范围')
    }
    return { id: key, externalId: key, source: 'shortcuts' as const, createdAt: snapshot.exportedAt, updatedAt: snapshot.exportedAt }
  }
  const activeDays = new Set<string>()
  const metrics = snapshot.metrics.map((item) => {
    const record = parseMetricInput(item.type === 'weight'
      ? { ...item, type: 'weight', inputUnit: 'kg' }
      : { ...item, type: item.type }, now)
    if (item.type === 'activeEnergy') {
      if (activeDays.has(record.localDay)) throw new Error('活动卡路里必须是每日唯一总量')
      activeDays.add(record.localDay)
    }
    return { ...record, ...metadata(`metric:${item.type}`, item.externalId, item.recordedAt) }
  })
  const sleeps: SleepRecord[] = []
  if (snapshot.sleepSamples?.length && snapshot.sleeps.length) throw new Error('睡眠快照不能混用区间和阶段')
  for (const item of snapshot.sleeps) {
    const record = parseSleepInput(item, sleeps.map((entry) => ({ ...entry, source: 'manual' })), now)
    sleeps.push({ ...record, ...metadata('sleep', item.externalId, item.endAt) })
  }
  if (snapshot.sleepSamples) sleeps.push(...shortcutSleepRecords(snapshot.sleepSamples, snapshot.exportedAt, now))
  const workouts: WorkoutRecord[] = snapshot.workouts.map((item) => ({
    ...parseWorkoutInput(item, now), ...metadata('workout', item.externalId, item.startedAt),
  }))
  for (const event of snapshot.calendarWorkouts ?? []) {
    const { record } = parseCalendarWorkout(event, now)
    if (keys.has(record.id)) throw new Error('快照含重复记录标识')
    keys.add(record.id)
    workouts.push(record)
  }
  return { snapshot, records: { metrics, sleeps, workouts } }
}

export async function readLimitedJson(body: ReadableStream<Uint8Array> | null, limit = MAX_SYNC_BYTES): Promise<unknown> {
  if (!body) throw new Error('缺少数据')
  const reader = body.getReader()
  const chunks: Uint8Array[] = []
  let size = 0
  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      size += value.length
      if (size > limit) {
        await reader.cancel()
        throw new Error('数据超过 256 KiB 限制')
      }
      chunks.push(value)
    }
  } finally { reader.releaseLock() }
  const bytes = new Uint8Array(size)
  let offset = 0
  for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.length }
  return JSON.parse(new TextDecoder('utf-8', { fatal: true, ignoreBOM: false }).decode(bytes))
}

/** Return schema locations and fixed error codes only, never user-supplied values. */
export function shortcutValidationFailure(error: unknown) {
  if (error instanceof z.ZodError) {
    const issue = error.issues[0]
    const allowed = new Set(['version', 'exportedAt', 'metrics', 'sleeps', 'sleepSamples', 'stage', 'source', 'workouts', 'externalId', 'type', 'value', 'recordedAt', 'timeZone', 'startAt', 'endAt', 'wakeTimeZone', 'activityType', 'startedAt', 'durationMinutes', 'activeEnergyKcal'])
    const field = issue?.path.filter((part) => typeof part === 'number' || (typeof part === 'string' && allowed.has(part))).join('.') || 'snapshot'
    return { code: 'INVALID_FIELD', field, reason: issue?.code ?? 'invalid' }
  }
  if (error instanceof SyntaxError) return { code: 'INVALID_JSON' }
  const known: Record<string, string> = {
    '数据超过 256 KiB 限制': 'BODY_TOO_LARGE', '缺少数据': 'EMPTY_BODY',
    '导出时间不能在未来': 'FUTURE_EXPORT', '快照含重复记录标识': 'DUPLICATE_ID',
    '记录超出快照的最近 30 天范围': 'OUTSIDE_30_DAYS',
    '活动卡路里必须是每日唯一总量': 'DUPLICATE_DAILY_ENERGY',
    '无法识别睡眠阶段或来源': 'UNKNOWN_SLEEP_STAGE',
    '未找到 Apple Watch 睡眠来源': 'NO_WATCH_SLEEP_SOURCE',
    '睡眠片段时间无效': 'INVALID_SLEEP_INTERVAL',
    '睡眠快照不能混用区间和阶段': 'MIXED_SLEEP_FORMAT',
  }
  return { code: error instanceof Error ? known[error.message] ?? 'INVALID_RECORD' : 'INVALID_RECORD' }
}
